import type { TurnoverBand } from './late-fees';

/**
 * HSN codes (goods) and SAC codes (services) on invoices and in GSTR-1's HSN summary (table 12).
 * Taxpayers with aggregate turnover up to ₹5 crore give at least 4 digits; above ₹5 crore, 6
 * (Notification 78/2020-Central Tax). Services are chapter 99: a 4-digit heading (9983) or a 6-digit
 * SAC (998314).
 */

export function hsnDigitsRequired(turnoverBand: TurnoverBand = 'upto_1_5cr'): 4 | 6 {
  return turnoverBand === 'above_5cr' ? 6 : 4;
}

export const isServiceCode = (code: string) => /^99(\d{2}|\d{4})$/.test(code);

export type HsnProblem = 'format' | 'too_short';

/** What's wrong with a code as a taxpayer in this band would give it (null when it's fine). */
export function hsnProblem(code: string, turnoverBand?: TurnoverBand): HsnProblem | null {
  if (!/^\d{4}$|^\d{6}$|^\d{8}$/.test(code)) return 'format';
  if (code.startsWith('99') && code.length > 6) return 'format';
  if (code.length < hsnDigitsRequired(turnoverBand)) return 'too_short';
  return null;
}

/**
 * Unit quantity codes (UQC) the GST portal accepts in the HSN summary. Services have no quantity: "NA".
 */
export const UQC: Readonly<Record<string, string>> = Object.freeze({
  BAG: 'Bags',
  BAL: 'Bale',
  BDL: 'Bundles',
  BKL: 'Buckles',
  BOU: 'Billion of units',
  BOX: 'Box',
  BTL: 'Bottles',
  BUN: 'Bunches',
  CAN: 'Cans',
  CBM: 'Cubic metres',
  CCM: 'Cubic centimetres',
  CMS: 'Centimetres',
  CTN: 'Cartons',
  DOZ: 'Dozens',
  DRM: 'Drums',
  GGK: 'Great gross',
  GMS: 'Grammes',
  GRS: 'Gross',
  GYD: 'Gross yards',
  KGS: 'Kilograms',
  KLR: 'Kilolitre',
  KME: 'Kilometre',
  LTR: 'Litres',
  MLT: 'Millilitre',
  MTR: 'Metres',
  MTS: 'Metric ton',
  NOS: 'Numbers',
  OTH: 'Others',
  PAC: 'Packs',
  PCS: 'Pieces',
  PRS: 'Pairs',
  QTL: 'Quintal',
  ROL: 'Rolls',
  SET: 'Sets',
  SQF: 'Square feet',
  SQM: 'Square metres',
  SQY: 'Square yards',
  TBS: 'Tablets',
  TGM: 'Ten gross',
  THD: 'Thousands',
  TON: 'Tonnes',
  TUB: 'Tubes',
  UGS: 'US gallons',
  UNT: 'Units',
  YDS: 'Yards',
  NA: 'Not applicable (services)',
});

export const isUqc = (code: string) => Object.hasOwn(UQC, code);

/** A code's rate where it depends on the price per unit (clothes and shoes: 5% up to ₹2,500 a piece). */
export interface PriceRule {
  /** Paise per unit. */
  priceLimit: number;
  rateAbove: number;
}

/** The rate for a unit price: `rate` up to the limit, `rateAbove` over it. */
export function rateForPrice(rate: number, rule: PriceRule | null, unitPrice: number): number {
  return rule && unitPrice > rule.priceLimit ? rule.rateAbove : rate;
}
